'use client';

import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';

import { MESSAGES } from '@/constants';

interface ComponentErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ComponentErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ComponentErrorBoundary extends Component<
  ComponentErrorBoundaryProps,
  ComponentErrorBoundaryState
> {
  constructor(props: ComponentErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ComponentErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(
      `ComponentErrorBoundary caught an error${this.props.componentName ? ` in ${this.props.componentName}` : ''}:`,
      error,
      errorInfo,
    );
    this.props.onError?.(error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorMessage = this.state.error?.message || MESSAGES.GENERAL.UNEXPECTED_ERROR;
      const title = this.props.componentName
        ? `${this.props.componentName} Failed`
        : 'Something went wrong';

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: '50vh',
            py: 8,
          }}
        >
          <Box sx={{ textAlign: 'center', maxWidth: 480 }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: 'text.primary' }}>
              {title}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              {errorMessage}
            </Typography>
            <Button
              variant="contained"
              onClick={this.handleReset}
              // No hardcoded bgcolor: `contained` + default color="primary"
              // already picks up the theme's CTA accent (blue) via the
              // `containedPrimary` override in mui-theme.ts.
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Try Again
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC to wrap a component with an Error Boundary
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ComponentErrorBoundaryProps, 'children'>,
) {
  const componentName =
    errorBoundaryProps?.componentName ||
    WrappedComponent.displayName ||
    WrappedComponent.name ||
    'Component';

  function BoundComponent(props: P) {
    return (
      <ComponentErrorBoundary {...errorBoundaryProps} componentName={componentName}>
        <WrappedComponent {...props} />
      </ComponentErrorBoundary>
    );
  }

  BoundComponent.displayName = `WithErrorBoundary(${componentName})`;
  return BoundComponent;
}

export default ComponentErrorBoundary;
